/* AI Builder — conversational intake.
   Paste ANY rough text (LinkedIn dump, bio, notes, JD snippets) and the AI
   assembles a complete resume. User can iterate with follow-up messages. */
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Sparkles, Send, Loader2, User, Bot, ClipboardPaste, FileUp } from "lucide-react";
import { toast } from "sonner";

const STARTERS = [
  { label: "Paste LinkedIn", hint: "Copy your LinkedIn 'About' + experience into the box below." },
  { label: "Rough notes", hint: "Jot down who you are, what you do, achievements — we'll rewrite it." },
  { label: "Old resume text", hint: "Paste text from an older CV — we'll standardize + rewrite." },
  { label: "Job description", hint: "Paste the JD and we'll generate a resume that fits it." },
];

export default function Builder() {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi! 👋 Paste any rough text about your background — LinkedIn dump, notes, an old résumé, even a job description you're targeting — and I'll assemble a polished, recruitment-ready resume in seconds. You can refine everything in the editor afterward." },
  ]);
  const [input, setInput] = useState("");
  const [targetRole, setTargetRole] = useState("");
  const [busy, setBusy] = useState(false);
  const uploadRef = useRef(null);
  const navigate = useNavigate();

  const appendMsg = (m) => setMessages((arr) => [...arr, m]);

  const canGenerate = input.trim().length >= 20;

  const onGenerate = async () => {
    if (!canGenerate) return;
    const text = input.trim();
    appendMsg({ role: "user", text });
    setInput("");
    setBusy(true);
    appendMsg({ role: "assistant", text: "Reading your input and drafting the resume — hang tight…" });
    try {
      const r = await api.post("/resumes/from-text", { text, target_role: targetRole });
      appendMsg({ role: "assistant", text: `✅ Done. Created "${r.data.title}" — opening the editor so you can refine it.` });
      toast.success("Resume drafted with AI");
      setTimeout(() => navigate(`/editor/${r.data.id}`), 600);
    } catch (e) {
      appendMsg({ role: "assistant", text: `❌ ${e?.response?.data?.detail || "Something went wrong. Try a longer input (20+ chars)."}` });
      toast.error("Generation failed");
    } finally {
      setBusy(false);
    }
  };

  const onPasteClipboard = async () => {
    try {
      const t = await navigator.clipboard.readText();
      if (t) { setInput((x) => (x ? x + "\n\n" + t : t)); toast.success("Pasted from clipboard"); }
      else toast.error("Clipboard empty");
    } catch { toast.error("Clipboard access denied"); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/resumes/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Resume parsed from file");
      navigate(`/editor/${r.data.id}`);
    } catch { toast.error("Upload failed"); } finally { setBusy(false); if (uploadRef.current) uploadRef.current.value = ""; }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="p-6 md:p-8 max-w-4xl mx-auto w-full flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground font-semibold flex items-center gap-2">
              <Sparkles className="size-3.5" /> AI Builder
            </div>
            <h1 className="display text-3xl md:text-4xl font-black tracking-tighter mt-1">Draft a resume from anything</h1>
            <p className="text-muted-foreground mt-1 text-sm">Paste a LinkedIn dump, rough notes, or upload a file — AI assembles a polished resume.</p>
          </div>
          <input type="file" accept=".pdf,.docx,.doc,.txt,.png,.jpg,.jpeg" ref={uploadRef} onChange={onUpload} className="hidden" data-testid="builder-file-input" />
          <Button variant="outline" onClick={() => uploadRef.current?.click()} className="gap-2 lift" disabled={busy} data-testid="builder-upload">
            <FileUp className="size-4" /> Upload file
          </Button>
        </div>

        {/* Starters */}
        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="builder-starters">
          {STARTERS.map((s) => (
            <button key={s.label} onClick={() => toast.info(s.hint)} className="text-left rounded-lg border border-border bg-card px-3 py-2 text-xs hover:bg-secondary lift" data-testid={`builder-starter-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="font-bold">{s.label}</div>
              <div className="text-muted-foreground text-[11px] mt-0.5">{s.hint}</div>
            </button>
          ))}
        </div>

        {/* Chat */}
        <Card className="border-border mt-5 flex-1 min-h-[300px]">
          <CardContent className="p-0">
            <div className="divide-y divide-border" data-testid="builder-messages">
              {messages.map((m, i) => (
                <div key={i} className={`px-5 py-4 flex gap-3 ${m.role === "assistant" ? "bg-secondary/30" : ""}`} data-testid={`builder-msg-${i}`}>
                  <div className={`size-7 rounded-full grid place-items-center shrink-0 ${m.role === "assistant" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                    {m.role === "assistant" ? <Bot className="size-4" /> : <User className="size-4" />}
                  </div>
                  <div className="flex-1 text-sm whitespace-pre-wrap leading-relaxed">{m.text}</div>
                </div>
              ))}
              {busy && (
                <div className="px-5 py-4 flex gap-3 bg-secondary/30">
                  <div className="size-7 rounded-full grid place-items-center bg-primary text-primary-foreground"><Loader2 className="size-4 animate-spin" /></div>
                  <div className="text-sm text-muted-foreground self-center">Thinking…</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Compose */}
        <div className="mt-4 space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2">
            <Input placeholder="Target role (optional) — e.g. Senior Product Manager" value={targetRole} onChange={(e) => setTargetRole(e.target.value)} data-testid="builder-target-role" />
            <Button variant="outline" onClick={onPasteClipboard} disabled={busy} className="gap-2 lift" data-testid="builder-paste"><ClipboardPaste className="size-4" /> Paste</Button>
          </div>
          <div className="relative">
            <Textarea
              rows={6}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Paste your LinkedIn 'About' + experience, or just ramble about what you've done…"
              className="pr-28"
              data-testid="builder-input"
            />
            <Button onClick={onGenerate} disabled={!canGenerate || busy} className="absolute bottom-2 right-2 gap-2 lift" data-testid="builder-generate">
              {busy ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />} Generate
            </Button>
          </div>
          <div className="text-[11px] text-muted-foreground">{input.length} chars · AI needs at least 20 characters to work well. Longer input = better results.</div>
        </div>
      </div>
    </div>
  );
}
